import { isServerSide } from "@jti/fe-common";
import { getAddressByIp, getUser } from "@jti/rest-api";
import { type IUser } from "@jti/rest-api-types";

export type StandardData = {
  event: string;
  section: string;
  user_id?: string;
  user_country?: string;
  user_language?: string;
  user_points_balance?: number;
  tier_level?: string;
  template_name?: string;
  referee?: string;
  user_membership: string;
};
export const templatesNames = [
  "account-information",
  "account-page",
  "account-ploom-club",
  "cart",
  "checkout",
  "communication-preferences",
  "contact-page",
  "faq-page",
  "flavor-finder",
  "free-trial-checkout",
  "free-trial-landing-page",
  "free-trial-PDP",
  "home-page",
  "login-page",
  "my-addresses",
  "my-devices",
  "my-orders",
  "my-subscription",
  "order-confirmation",
  "other",
  "get-started",
  "ploom-club-article",
  "ploom-club-blog",
  "ploom-club-earn",
  "ploom-club-overview",
  "ploom-club-pack-codes",
  "ploom-club-points-history",
  "ploom-club-games",
  "ploom-club-games-list",
  "ploom-club-redeem",
  "ploom-design-house",
  "ploom-hav-page",
  "qr-age-verification-popup",
  "product-details-page",
  "product-marketing-page",
  "referral-page",
  "search-results",
  "shop",
  "signup_regular",
  "store-locator",
  "unsubscribe",
  "blog",
  "article-page",
] as const;

const namesMapping: Record<string, TemplateNames> = {
  homepage: "home-page",
};

export type TemplateNames = (typeof templatesNames)[number];
const isValidateTemplateName = (name: string): name is TemplateNames => templatesNames.includes(name as TemplateNames);
const validateTemplateName = (name?: string): TemplateNames => {
  if (!name) {
    return "other";
  }
  return isValidateTemplateName(name) ? name : namesMapping[name] || "other";
};

export class GoogleTagManagerEvents {
  gtmContainer: HTMLElement | null = null;
  templateName: string;
  country: string;
  user: IUser;
  userPoints: number;
  userTier: string;
  referee: string;
  callbackRun: boolean;
  pageLang: string | undefined;
  user_membership: string;
  isReady: boolean;

  constructor(name: string, callback: () => void, points: number, referee: string, tier: string) {
    if (!isServerSide()) {
      this.gtmContainer = document.getElementById("gtmContainer");
      this.pageLang = document.querySelector("html")?.getAttribute("lang") ?? "";
    }

    this.templateName = validateTemplateName(name);
    this.country = "";
    this.user = getUser();
    this.userPoints = points;
    this.userTier = tier;
    this.referee = referee;
    this.callbackRun = false;
    this.user_membership = "N/A";
    this.isReady = false;

    const runCallback = () => {
      if (!this.callbackRun) {
        callback();
        this.callbackRun = true;
      }
    };

    if (!isServerSide() && this.gtmContainer) {
      const gtmContainerObserver = new MutationObserver(() => this.updateTemplateName());
      gtmContainerObserver.observe(this.gtmContainer, { attributes: true });
    }
    getAddressByIp()
      .then((response) => {
        this.country = response?.countryRegion?.isoCode || "";
      })
      .finally(() => {
        this.isReady = true;
        runCallback();
      });
  }

  updateTemplateName() {
    this.templateName = validateTemplateName(this.gtmContainer?.dataset.pageName);
  }

  getStandardData(event: string): StandardData {
    return {
      event,
      section: this.templateName,
      user_id: this.user?.uuid || undefined,
      user_country: this.country || undefined,
      user_language: this.pageLang || undefined,
      user_points_balance: this.user?.uuid ? this.userPoints : undefined,
      tier_level: this.user?.uuid ? this.userTier : undefined,
      template_name: this.templateName || undefined,
      referee: this.referee || undefined,
      user_membership: this.user_membership,
    };
  }
}
